import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbDropdownConfig } from '@ng-bootstrap/ng-bootstrap';
import { PataviumRole } from '../../models/request-and-responses/rest-api/enums/role.enum.model';
import { DataService } from '../../services/data.service';
import { Subscription } from 'rxjs/Subscription';
import { ToastrService } from 'ngx-toastr';
import { HeaderService } from '../../services/header.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { tap } from 'rxjs/operators';
import { WebSocketMessageService } from '../../services/web-socket/web-socket-message.service';
import {
    ContractStateChangeMessage,
    IWrappedContractStateChangeMessage
} from '../../helpers/socket-messages/contract-state-change-message';
import { HeaderNotification, HeaderNotificationTopic } from '../../helpers/header-notification';
import { UserSessionInfo } from '../../models/frontend/user.session-info';
import { IInternalEvent, InternalEventsService } from '../../services/internal-events.service';
import { AuthService } from '../../services/auth.service';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.css'],
    providers: [NgbDropdownConfig]
})
export class HeaderComponent implements OnInit, OnDestroy {
    HeaderNotificationTopic = HeaderNotificationTopic;
    PataviumRole = PataviumRole;
    public contract_messages: HeaderNotification[] = [];
    public avatar: string;
    public updateAvatarSubscription: Subscription;
    public menu = { documents: true, contracts: true, chat: false };
    public sessionInfo: UserSessionInfo;
    private readonly contractsUpdatedEventSubscription: Subscription;

    constructor(
        private ngbDropdownConfig: NgbDropdownConfig,
        private router: Router,
        private dataService: DataService,
        private toastr: ToastrService,
        private headerService: HeaderService,
        private sessionStorageService: SessionStorageService,
        private webSocketMessageService: WebSocketMessageService,
        private internalEventsService: InternalEventsService,
        private authService: AuthService
    ) {
        ngbDropdownConfig.placement = 'bottom-right';
        ngbDropdownConfig.autoClose = true;
        this.sessionInfo = SessionStorageService.sessionInfo;
        //subscribe avatar changes
        this.updateAvatarSubscription = this.headerService
            .updateAvatarObservable()
            .subscribe(next => this.getAvatar());
        this.subscribeAndHandleInterestingMessages();

        this.contractsUpdatedEventSubscription = this.internalEventsService.EVENTS.UPDATE_DATA.CONTRACTS.DONE
            .observable
            .pipe(tap(async ev => {
                console.debug(`Triggered event ${ev.description}`);
                await this.filterReadMessages(HeaderNotificationTopic.Contract);
            }))
            .subscribe();
    }

    ngOnInit() {
        this.getAvatar();
        this.setMenuVisibility();
    }

    ngOnDestroy(): void {
        if (this.updateAvatarSubscription) {
            this.updateAvatarSubscription.unsubscribe();
        }
        if (this.contractsUpdatedEventSubscription) {
            this.contractsUpdatedEventSubscription.unsubscribe();
        }
    }

    // logout
    get profileRoute(): string {
        switch (this.sessionInfo.role) {
            case PataviumRole.Admin:
                return '/superadmin/profile';
            case PataviumRole.BackOfficeOperator:
                return '/admin/profile';
            case PataviumRole.Agency:
                return '/agency/profile';
            case PataviumRole.AgencyOperator:
                return '/agency-operator/profile';
            case PataviumRole.Agent:
                return '/agent/profile';
            case PataviumRole.SpecializedOperator:
                return '/specialized-operator/profile';
            default:
                console.error(
                    `'${this.sessionInfo.role}' non è un ruolo valido: impossibile ottenere la rotta per il profilo`
                );
                return null;
        }
    }

    async logout() {
        await this.authService.logout();
    }

    async markMessageAsRead(message: HeaderNotification, emitEvent: boolean = true) {
        const innerMessageClass = message.innerMessage.constructor.name;
        console.debug(`Marking as read message with inner message class= ${innerMessageClass}`);
        if (innerMessageClass === ContractStateChangeMessage.name) {
            console.log(`Marking as read ${ContractStateChangeMessage.name}:`, message);

            this.contract_messages = this.contract_messages.map(m => {
                if (m.notificationId.equals(message.notificationId)) {
                    m.read = true;
                }
                return m;
            });
            if (emitEvent) {
                this.internalEventsService.EVENTS.NOTIFICATIONS.CONTRACTS.READ.emit();
            }
        } else {
            console.error(`Cannot read unknown type message ${innerMessageClass}`, message);
        }
    }

    async markAllMessagesAsRead(topic: HeaderNotificationTopic) {
        console.info(`Read all messages`);
        let msgList: HeaderNotification[] = [];
        let event: IInternalEvent = null;
        switch (topic) {
            case HeaderNotificationTopic.Contract:
                msgList = this.contract_messages;
                event = this.internalEventsService.EVENTS.NOTIFICATIONS.CONTRACTS.READ;
                break;
            default:
                console.warn(`Cannot read messages: the topic '${topic}' is not handled`);
                return;
        }
        // read all messages
        for (const msg of msgList) {
            await this.markMessageAsRead(msg, false);
        }
        // emit only one event
            event.emit();
    }

    private _contractStateChangeMessageHandler(message: IWrappedContractStateChangeMessage) {
        try {
            const contractMessage = HeaderNotification.fromContractStateChangeMessage(
                message.message
            );
            this.contract_messages.push(contractMessage);
        } catch (e) {
            console.error(`Error handling message:`, message);
        }
    }

    private getAvatar() {
        this.dataService.getAvatarCurrentUser().subscribe(
            avatar => {
                this.avatar = 'data:image/png;base64,' + avatar.payload[0].thumbnail;
            },
            err => {}
        );
    }

    private setMenuVisibility() {
        switch (this.sessionInfo.role) {
            case PataviumRole.Admin:
                this.menu.chat = false;
                this.menu.documents = false;
                this.menu.contracts = false;
                break;
        }
    }

    private subscribeAndHandleInterestingMessages() {
        //contract state change
        this.webSocketMessageService
            .EVENTS
            .CONTRACT_STATE_CHANGE
            .observable
            .pipe(
                tap(msg => {
                    this._contractStateChangeMessageHandler(msg);
                })
            )
            .subscribe();
    }

    private async filterReadMessages(topic: HeaderNotificationTopic) {
        console.debug(`Filtering read messages for topic ${topic}`);
        const filterRead = (list: HeaderNotification[]): HeaderNotification[] => list.filter(m  => !m.read);
        switch (topic) {
            case HeaderNotificationTopic.Contract:
                this.contract_messages = filterRead(this.contract_messages);
                break;
            default:
                console.warn(`Cannot filter read messages: the topic '${topic}' is not handled`);
                return;
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/components/header/header.component.ts